import {
	ClockIcon,
	ExclamationCircleIcon,
	XCircleIcon,
} from "@heroicons/react/24/outline";
import { cva } from "class-variance-authority";
import type { ComponentProps, MouseEvent } from "react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { DataStateBoundary } from "src/components/DataStateBoundary";
import {
	Dialog,
	DialogConfirmation,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "src/components/Dialog";
import { SimpleLoadingScreen } from "src/components/LoadingScreen";
import { Button } from "src/components/ui/Button";
import {
	type FeedbackVariantsProps,
	feedbackVariants,
} from "src/components/utils/feedback.variants";
import { useOnTimeout } from "src/hooks/use-on-timeout";
import { useOidcLogin } from "src/services/oidc-exporter";
import { isProd } from "src/settings";
import {
	AUTH_CONSTANTS,
	redirectToSignIn,
	resetAuthentication,
	resetAuthenticationFallback,
} from "src/utils/auth";
import { cn } from "src/utils/tailwind";

type EventName =
	| "session-lost"
	| "session-expire"
	| "auth-error"
	| "auth-success"
	| "auth-in-progress";

type OidcErrorProps = ComponentProps<"div"> &
	FeedbackVariantsProps & {
		eventTranslationKey?: string;
		eventName: EventName;
		icon: typeof ExclamationCircleIcon;
		loginBtn?: boolean;
	};

export const oidcErrorVariants = cva(["h-20 w-20"], {
	variants: {
		variant: {
			"session-lost": "text-yellow-500",
			"session-expire": "text-gray-500",
			"auth-error": "text-red-500",
			"auth-success": "text-green-500",
			"auth-in-progress": "text-gray-500",
		},
	},
	defaultVariants: {
		variant: "auth-error",
	},
});

const useResetAuthentication = () => {
	const [showResetDialog, setShowResetDialog] = useState(false);

	const handleResetClick = (event: MouseEvent<HTMLButtonElement>) => {
		event.preventDefault();
		setShowResetDialog(true);
	};

	const handleResetConfirm = async () => {
		setShowResetDialog(false);
		await resetAuthentication();
	};

	return {
		showResetDialog,
		setShowResetDialog,
		handleResetClick,
		handleResetConfirm,
	};
};

function ResetConfirmationDialog({
	isOpen,
	onOpenChange,
	onConfirm,
}: {
	isOpen: boolean;
	onOpenChange: (open: boolean) => void;
	onConfirm: () => void;
}) {
	const { t } = useTranslation();

	return (
		<Dialog open={isOpen} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>{t("oidc.resetConfirmTitle")}</DialogTitle>
					<DialogDescription>
						{t("oidc.resetConfirmDescription")}
					</DialogDescription>
				</DialogHeader>
				<DialogFooter>
					<DialogConfirmation
						onConfirm={onConfirm}
						onCancel={() => onOpenChange(false)}
						confirmText={t("oidc.resetConfirm")}
						cancelText={t("cancel")}
						variant="danger"
						matomoEventCategory="authentication"
						matomoEventName="reset-auth-data"
					/>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

function OidcErrorButtonsFallback({
	loginBtn,
	onLogin,
	showResetButton = true,
}: {
	loginBtn?: boolean;
	onLogin: () => void;
	showResetButton?: boolean;
}) {
	const { t } = useTranslation();

	const handleResetClick = (event: MouseEvent<HTMLButtonElement>) => {
		event.preventDefault();
		resetAuthenticationFallback();
	};

	return (
		<div className="flex flex-col gap-3 w-full max-w-xs mx-auto">
			{loginBtn && (
				<Button
					variant="primary"
					onClick={onLogin}
					data-testid="login-button"
					className="w-full justify-center"
				>
					{t("login")}
				</Button>
			)}
			{showResetButton && (
				<Button
					variant="secondary"
					onClick={handleResetClick}
					data-testid="reset-button"
					className="w-full justify-center flex items-center gap-2 text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
				>
					<XCircleIcon className="h-4 w-4" />
					{t("oidc.resetAuthentication")}
				</Button>
			)}
		</div>
	);
}

function OidcErrorButtons({
	loginBtn,
	onLogin,
	showResetButton = true,
}: {
	loginBtn?: boolean;
	onLogin: () => void;
	showResetButton?: boolean;
}) {
	const { t } = useTranslation();
	const {
		showResetDialog,
		setShowResetDialog,
		handleResetClick,
		handleResetConfirm,
	} = useResetAuthentication();

	return (
		<>
			<div className="flex flex-col gap-3 w-full max-w-xs mx-auto">
				{loginBtn && (
					<Button
						variant="primary"
						onClick={onLogin}
						data-testid="login-button"
						className="w-full justify-center"
					>
						{t("login")}
					</Button>
				)}
				{showResetButton && (
					<Button
						variant="secondary"
						onClick={handleResetClick}
						data-testid="reset-button"
						className="w-full justify-center flex items-center gap-2 text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
					>
						<XCircleIcon className="h-4 w-4" />
						{t("oidc.resetAuthentication")}
					</Button>
				)}
			</div>

			<ResetConfirmationDialog
				isOpen={showResetDialog}
				onOpenChange={setShowResetDialog}
				onConfirm={handleResetConfirm}
			/>
		</>
	);
}

function OidcErrorFallback({
	variant = "fit",
	eventName,
	icon: Icon,
	loginBtn = false,
	className,
}: Omit<OidcErrorProps, "eventTranslationKey"> & { className?: string }) {
	const { t } = useTranslation();

	const handleLogin = () => {
		redirectToSignIn();
	};

	return (
		<div
			data-testid="session-lost"
			className={cn(
				feedbackVariants({ variant }),
				"h-screen border-transparent bg-pl-light-slate",
				className,
			)}
		>
			<Icon className={oidcErrorVariants({ variant: eventName })} />
			<div className="text-center m-4 space-y-2">
				<p>{t("oidc.authenticationError")}</p>
				<p className="text-sm text-gray-600">
					{t("oidc.authenticationErrorHelp")}
				</p>
			</div>
			<OidcErrorButtonsFallback
				loginBtn={loginBtn}
				onLogin={handleLogin}
			/>
		</div>
	);
}

function OidcErrorInternal({
	variant = "fit",
	eventTranslationKey,
	eventName,
	icon: Icon,
	loginBtn = false,
	...others
}: OidcErrorProps) {
	const login = useOidcLogin();
	const { t } = useTranslation();

	return (
		<div
			data-testid="session-lost"
			className={cn(
				feedbackVariants({ variant }),
				"h-screen border-transparent bg-pl-light-slate",
			)}
			{...others}
		>
			<Icon className={oidcErrorVariants({ variant: eventName })} />
			{eventTranslationKey && (
				<p className="text-center m-4">{t(eventTranslationKey)}</p>
			)}
			<OidcErrorButtons
				loginBtn={loginBtn}
				onLogin={() => login()}
				showResetButton={!isProd()} // only show reset button in in dev for testing first
			/>
		</div>
	);
}

export function OidcError(props: OidcErrorProps) {
	return <OidcErrorInternal {...props} />;
}

export function SessionLost() {
	return (
		<DataStateBoundary>
			<OidcError
				eventTranslationKey="oidc.sessionLost"
				eventName="session-lost"
				icon={ExclamationCircleIcon}
				loginBtn
			/>
		</DataStateBoundary>
	);
}

export function SessionExpired() {
	return (
		<DataStateBoundary>
			<OidcError
				eventTranslationKey="oidc.sessionExpired"
				eventName="session-expire"
				icon={ClockIcon}
				loginBtn
			/>
		</DataStateBoundary>
	);
}

export function AuthenticationError() {
	return (
		<DataStateBoundary>
			<OidcErrorFallback
				eventName="auth-error"
				icon={XCircleIcon}
				loginBtn={true}
			/>
		</DataStateBoundary>
	);
}

export function AuthenticationLoading() {
	return (
		<SimpleLoadingScreen
			className="h-screen border-transparent bg-pl-light-slate"
			variant="fit"
		/>
	);
}

export function AuthenticationSuccess() {
	const retryCountRef = useRef(0);

	useOnTimeout(() => {
		// authentication success should redirect you to the page.
		// but the redirection fails sometimes (on safari).
		// make sure to reload the page on certain timeout. That should trigger redirect user.
		retryCountRef.current += 1;

		if (retryCountRef.current <= AUTH_CONSTANTS.MAX_RETRY_ATTEMPTS) {
			window.location.reload();
		} else {
			// If reload doesn't work after max attempts, try direct redirect
			redirectToSignIn();
		}
	}, AUTH_CONSTANTS.AUTH_SUCCESS_TIMEOUT);

	return <AuthenticationLoading />;
}
